/**
 * Central Promotions Engine — access logic (Growth Platform — Slice S5).
 *
 * The ONLY module the UI imports to read promotions. It applies the business
 * rules the spec requires (status gating, date windows, eligibility filtering)
 * so components never reason about "is this promo live?" themselves.
 *
 * Rules enforced here (spec rules 8–11, §5):
 *  - status must be "active" (draft/archived are never public).
 *  - if startDate/endDate are set, "now" must fall within the window.
 * When the MERN backend lands, swap the PROMOTIONS import for a fetch to
 * /api/promotions — every call site below stays identical.
 */

import { PROMOTIONS } from "../data/promotionsData";

/** True if a promotion is publicly live right now. */
export function isPromotionLive(promo, now = new Date()) {
  if (!promo || promo.status !== "active") return false;

  if (promo.startDate) {
    const start = new Date(promo.startDate);
    if (now < start) return false;
  }
  if (promo.endDate) {
    // Treat endDate as inclusive through end of that day.
    const end = new Date(promo.endDate);
    end.setHours(23, 59, 59, 999);
    if (now > end) return false;
  }
  return true;
}

/** All currently-live promotions. */
export function getActivePromotions(now = new Date()) {
  return PROMOTIONS.filter((p) => isPromotionLive(p, now));
}

/** Live promotions flagged `featured` (for homepage / hero slots). */
export function getFeaturedPromotions(now = new Date()) {
  return getActivePromotions(now).filter((p) => p.featured);
}

/** Live promotions for a given eligibility group (e.g. "student"). */
export function getPromotionsByEligibility(eligibility, now = new Date()) {
  return getActivePromotions(now).filter(
    (p) => p.eligibility === eligibility || p.eligibility === "all"
  );
}

/** Look up a single live promotion by id (null if not found or not live). */
export function getPromotionById(id, now = new Date()) {
  const promo = PROMOTIONS.find((p) => p.id === id);
  return promo && isPromotionLive(promo, now) ? promo : null;
}
